import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { X, Tag, CheckSquare, ChatCircle, Paperclip, UsersThree, Clock, Plus, Trash, Check, PencilSimple, Buildings, User, CurrencyDollar, ClockCounterClockwise, LinkSimple, UploadSimple, Warning, DotsThreeVertical, Copy, Archive, ArrowsDownUp, DotsSixVertical } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function CardDetailPanel({ neg: initialNeg, stages, contacts, allLabels, allUsers, onClose, onUpdated, onNavigateToContact, onLabelsChanged, onArchived, onCopied }) {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [neg, setNeg] = useState(initialNeg);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [history, setHistory] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [newChecklistTitle, setNewChecklistTitle] = useState("Checklist");
  const [newItemText, setNewItemText] = useState({});
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [dragCheckItem, setDragCheckItem] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/negociaciones/${neg.id}/comments`, { headers }),
      axios.get(`${API}/negociaciones/${neg.id}/history`, { headers }),
    ]).then(([cRes, hRes]) => {
      setComments(cRes.data || []);
      setHistory(hRes.data || []);
    }).catch(() => {});
  }, [neg.id]);

  const refreshNeg = async () => {
    const r = await axios.get(`${API}/negociaciones/${neg.id}`, { headers });
    if (r.data) { setNeg(r.data); onUpdated?.(r.data); }
  };

  const startEdit = () => {
    setEditMode(true);
    setEditData({
      title: neg.title || "", amount: neg.amount || 0, notes: neg.notes || "",
      stage: neg.stage, payment_status: neg.payment_status || "",
      contact_id: neg.contact_id || "",
    });
  };

  const saveEdit = async () => {
    try {
      const contactObj = contacts.find(c => c.id === editData.contact_id);
      const res = await axios.put(`${API}/negociaciones/${neg.id}`, {
        ...editData, amount: parseFloat(editData.amount) || 0,
        company_name: contactObj?.company_name || neg.company_name || "",
        contact_name: contactObj?.contact_name || neg.contact_name || "",
      }, { headers });
      setNeg(res.data);
      onUpdated?.(res.data);
      setEditMode(false);
      toast.success("Guardado");
    } catch { toast.error("Error al guardar"); }
  };

  // Labels
  const toggleLabel = async (labelId) => {
    const current = neg.label_ids || [];
    const next = current.includes(labelId) ? current.filter(id => id !== labelId) : [...current, labelId];
    const res = await axios.put(`${API}/negociaciones/${neg.id}/labels`, { label_ids: next }, { headers });
    setNeg(res.data);
    onUpdated?.(res.data);
  };

  // Dates
  const saveDates = async (startDate, dueDate) => {
    const res = await axios.put(`${API}/negociaciones/${neg.id}/dates`, { start_date: startDate, due_date: dueDate }, { headers });
    setNeg(res.data);
    onUpdated?.(res.data);
    toast.success("Fechas guardadas");
  };

  // Members
  const toggleMember = async (memberId) => {
    const current = neg.member_ids || [];
    const next = current.includes(memberId) ? current.filter(id => id !== memberId) : [...current, memberId];
    const res = await axios.put(`${API}/negociaciones/${neg.id}/members`, { member_ids: next }, { headers });
    setNeg(res.data);
    onUpdated?.(res.data);
  };

  // Checklists
  const addChecklist = async () => {
    try {
      const res = await axios.post(`${API}/negociaciones/${neg.id}/checklists`, { title: newChecklistTitle }, { headers });
      setNeg(res.data);
      onUpdated?.(res.data);
      setActiveSection(null);
      setNewChecklistTitle("Checklist");
    } catch { toast.error("Error"); }
  };

  const deleteChecklist = async (clId) => {
    const res = await axios.delete(`${API}/negociaciones/${neg.id}/checklists/${clId}`, { headers });
    setNeg(res.data); onUpdated?.(res.data);
  };

  const addChecklistItem = async (clId) => {
    const text = (newItemText[clId] || "").trim();
    if (!text) return;
    const res = await axios.post(`${API}/negociaciones/${neg.id}/checklists/${clId}/items`, { text }, { headers });
    setNeg(res.data); onUpdated?.(res.data);
    setNewItemText(prev => ({ ...prev, [clId]: "" }));
  };

  const toggleCheckItem = async (clId, itemId, checked) => {
    const res = await axios.put(`${API}/negociaciones/${neg.id}/checklists/${clId}/items/${itemId}`, { checked: !checked }, { headers });
    setNeg(res.data); onUpdated?.(res.data);
  };

  const deleteCheckItem = async (clId, itemId) => {
    const res = await axios.delete(`${API}/negociaciones/${neg.id}/checklists/${clId}/items/${itemId}`, { headers });
    setNeg(res.data); onUpdated?.(res.data);
  };

  // Comments
  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await axios.post(`${API}/negociaciones/${neg.id}/comments`, { text: newComment }, { headers });
      setComments(prev => [res.data, ...prev]);
      setNewComment("");
    } catch { toast.error("Error"); }
  };

  const deleteComment = async (cId) => {
    await axios.delete(`${API}/negociaciones/${neg.id}/comments/${cId}`, { headers });
    setComments(prev => prev.filter(c => c.id !== cId));
  };

  // Attachments
  const addLink = async () => {
    if (!linkUrl.trim()) return;
    try {
      const res = await axios.post(`${API}/negociaciones/${neg.id}/attachments/link`, { url: linkUrl, name: linkName }, { headers });
      setNeg(prev => ({ ...prev, attachments: [...(prev.attachments || []), res.data] }));
      setLinkUrl(""); setLinkName(""); setActiveSection(null);
      toast.success("Enlace agregado");
    } catch { toast.error("Error"); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/negociaciones/${neg.id}/attachments/file`, form, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setNeg(prev => ({ ...prev, attachments: [...(prev.attachments || []), res.data] }));
      toast.success("Archivo subido");
    } catch { toast.error("Error al subir"); }
  };

  const deleteAttachment = async (attId) => {
    await axios.delete(`${API}/negociaciones/${neg.id}/attachments/${attId}`, { headers });
    setNeg(prev => ({ ...prev, attachments: (prev.attachments || []).filter(a => a.id !== attId) }));
  };

  // Archive
  const handleArchive = async () => {
    try {
      const res = await axios.patch(`${API}/negociaciones/${neg.id}/archive`, { archived: !neg.archived }, { headers });
      toast.success(neg.archived ? "Tarjeta restaurada" : "Tarjeta archivada");
      onArchived?.(res.data);
      if (!neg.archived) onClose();
      else { setNeg(res.data); onUpdated?.(res.data); }
    } catch { toast.error("Error"); }
    setShowMenu(false);
  };

  // Copy
  const handleCopy = async () => {
    try {
      const res = await axios.post(`${API}/negociaciones/${neg.id}/copy`, {}, { headers });
      toast.success(`Copia creada: ${res.data.number}`);
      onCopied?.(res.data);
    } catch { toast.error("Error al copiar"); }
    setShowMenu(false);
  };

  // Move (change stage)
  const handleMove = async (stageId) => {
    try {
      await axios.patch(`${API}/negociaciones/${neg.id}/stage`, { stage: stageId }, { headers });
      const updated = { ...neg, stage: stageId };
      setNeg(updated);
      onUpdated?.(updated);
      toast.success("Tarjeta movida");
    } catch { toast.error("Error al mover"); }
    setShowMoveMenu(false);
    setShowMenu(false);
  };

  // Reorder checklist items via drag
  const handleCheckDragStart = (clId, itemId) => {
    setDragCheckItem({ clId, itemId });
  };

  const handleCheckDrop = async (clId, targetItemId) => {
    if (!dragCheckItem || dragCheckItem.clId !== clId || dragCheckItem.itemId === targetItemId) {
      setDragCheckItem(null);
      return;
    }
    const cl = (neg.checklists || []).find(c => c.id === clId);
    if (!cl) return;
    const items = [...cl.items];
    const fromIdx = items.findIndex(i => i.id === dragCheckItem.itemId);
    const toIdx = items.findIndex(i => i.id === targetItemId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    // Optimistic update
    const updatedChecklists = (neg.checklists || []).map(c => c.id === clId ? { ...c, items } : c);
    setNeg(prev => ({ ...prev, checklists: updatedChecklists }));
    try {
      const res = await axios.put(`${API}/negociaciones/${neg.id}/checklists/${clId}/reorder`, { item_ids: items.map(i => i.id) }, { headers });
      setNeg(res.data); onUpdated?.(res.data);
    } catch { toast.error("Error al reordenar"); }
    setDragCheckItem(null);
  };

  const stageObj = stages.find(s => s.id === neg.stage);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="neg-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-slate-400">{neg.number}</p>
            <p className="text-sm font-bold text-slate-900 truncate">{neg.title || neg.number}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!editMode && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit} data-testid="neg-edit-btn"><PencilSimple size={12} className="mr-1" /> Editar</Button>}
            {/* "..." Menu */}
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="neg-menu-btn">
                <DotsThreeVertical size={18} weight="bold" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => { setShowMenu(false); setShowMoveMenu(false); }} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border rounded-xl shadow-xl w-48 py-1" data-testid="neg-menu-dropdown">
                    {/* Mover */}
                    <div className="relative">
                      <button onClick={() => setShowMoveMenu(!showMoveMenu)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors" data-testid="neg-menu-move">
                        <ArrowsDownUp size={14} /> Mover a etapa
                      </button>
                      {showMoveMenu && (
                        <div className="absolute left-full top-0 ml-1 bg-white border rounded-xl shadow-xl w-44 py-1 z-30">
                          {stages.map(s => (
                            <button key={s.id} onClick={() => handleMove(s.id)}
                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors ${s.id === neg.stage ? "font-bold text-blue-600" : "text-slate-700"}`}>
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                              {s.name} {s.id === neg.stage && <Check size={10} />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Copiar */}
                    <button onClick={handleCopy}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors" data-testid="neg-menu-copy">
                      <Copy size={14} /> Copiar tarjeta
                    </button>
                    <div className="border-t my-1" />
                    {/* Archivar */}
                    <button onClick={handleArchive}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors" data-testid="neg-menu-archive">
                      <Archive size={14} /> {neg.archived ? "Restaurar" : "Archivar"}
                    </button>
                  </div>
                </>
              )}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {editMode ? (
            <div className="space-y-3" data-testid="neg-edit-form">
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Titulo</label><Input value={editData.title} onChange={e => setEditData({...editData, title: e.target.value})} className="h-8 text-sm mt-1" /></div>
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Monto ($)</label><Input type="number" value={editData.amount} onChange={e => setEditData({...editData, amount: e.target.value})} className="h-8 text-sm mt-1" /></div>
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Etapa</label>
                <Select value={editData.stage} onValueChange={v => setEditData({...editData, stage: v})}>
                  <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}><span className="text-sm">{s.name}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Notas</label><Textarea value={editData.notes} onChange={e => setEditData({...editData, notes: e.target.value})} className="text-sm mt-1 min-h-[80px]" /></div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" className="flex-1 h-8 text-xs" onClick={saveEdit}>Guardar</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditMode(false)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Amount + Stage */}
              {neg.archived && (
                <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <Archive size={16} className="text-amber-600" />
                  <span className="text-xs font-bold text-amber-800">Esta tarjeta esta archivada</span>
                  <button onClick={handleArchive} className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-800">Restaurar</button>
                </div>
              )}
              <div className="text-center py-3 bg-slate-50 rounded-xl">
                <p className="text-3xl font-extrabold text-slate-900">${(neg.amount || 0).toLocaleString("es-MX")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {stageObj && <span className="text-xs font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: stageObj.color + "15", color: stageObj.color }}>{stageObj.name}</span>}
                {neg.payment_status && <span className={`text-xs font-bold px-3 py-1.5 rounded-lg ${neg.payment_status === "pagado" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{neg.payment_status === "pagado" ? "Pagado" : "No Pagado"}</span>}
              </div>

              {/* Toolbar - Trello style */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "labels", icon: Tag, label: "Etiquetas", color: "text-purple-600 bg-purple-50 hover:bg-purple-100" },
                  { id: "dates", icon: Clock, label: "Fechas", color: "text-amber-600 bg-amber-50 hover:bg-amber-100" },
                  { id: "checklist", icon: CheckSquare, label: "Checklist", color: "text-blue-600 bg-blue-50 hover:bg-blue-100" },
                  { id: "members", icon: UsersThree, label: "Miembros", color: "text-teal-600 bg-teal-50 hover:bg-teal-100" },
                  { id: "attach", icon: Paperclip, label: "Adjunto", color: "text-slate-600 bg-slate-100 hover:bg-slate-200" },
                ].map(btn => (
                  <button key={btn.id} onClick={() => setActiveSection(activeSection === btn.id ? null : btn.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeSection === btn.id ? "ring-2 ring-blue-400 " : ""}${btn.color}`}
                    data-testid={`toolbar-${btn.id}`}>
                    <btn.icon size={14} weight="bold" /> {btn.label}
                  </button>
                ))}
              </div>

              {/* Labels Panel */}
              {activeSection === "labels" && (
                <div className="border rounded-xl p-3 space-y-2 bg-slate-50" data-testid="labels-panel">
                  <p className="text-xs font-bold text-slate-700">Etiquetas</p>
                  {allLabels.length === 0 && <p className="text-xs text-slate-400">Sin etiquetas. Un admin puede crearlas.</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {allLabels.map(lb => {
                      const active = (neg.label_ids || []).includes(lb.id);
                      return (
                        <button key={lb.id} onClick={() => toggleLabel(lb.id)}
                          className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-all ${active ? "ring-2 ring-offset-1 ring-blue-400" : "opacity-60 hover:opacity-100"}`}
                          style={{ backgroundColor: lb.color + "20", color: lb.color }}>
                          {active && <Check size={10} weight="bold" />} {lb.name}
                        </button>
                      );
                    })}
                  </div>
                  {(user?.role === "admin" || user?.role === "subgerente") && <NewLabelForm headers={headers} onCreated={(lb) => { onLabelsChanged?.(); setNeg(prev => prev); }} />}
                </div>
              )}

              {/* Dates Panel */}
              {activeSection === "dates" && (
                <DatesPanel neg={neg} onSave={saveDates} onClose={() => setActiveSection(null)} />
              )}

              {/* Checklist Panel */}
              {activeSection === "checklist" && (
                <div className="border rounded-xl p-3 space-y-2 bg-slate-50" data-testid="checklist-add-panel">
                  <p className="text-xs font-bold text-slate-700">Agregar Checklist</p>
                  <Input value={newChecklistTitle} onChange={e => setNewChecklistTitle(e.target.value)} placeholder="Titulo del checklist..." className="h-8 text-sm" />
                  <Button size="sm" className="h-8 text-xs" onClick={addChecklist}>Agregar</Button>
                </div>
              )}

              {/* Members Panel */}
              {activeSection === "members" && (
                <div className="border rounded-xl p-3 space-y-2 bg-slate-50" data-testid="members-panel">
                  <p className="text-xs font-bold text-slate-700">Asignar Miembros</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {allUsers.map(u => {
                      const active = (neg.member_ids || []).includes(u.id);
                      return (
                        <button key={u.id} onClick={() => toggleMember(u.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${active ? "bg-blue-100 text-blue-800" : "hover:bg-slate-100 text-slate-700"}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${active ? "bg-blue-600 text-white" : "bg-slate-300 text-white"}`}>
                            {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
                          </div>
                          <span className="flex-1 text-left font-medium">{u.name || u.email}</span>
                          {active && <Check size={14} weight="bold" className="text-blue-600" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Attachment Panel */}
              {activeSection === "attach" && (
                <div className="border rounded-xl p-3 space-y-3 bg-slate-50" data-testid="attach-panel">
                  <p className="text-xs font-bold text-slate-700">Adjuntar</p>
                  <div>
                    <Button size="sm" variant="outline" className="h-8 text-xs w-full" onClick={() => fileRef.current?.click()}>
                      <UploadSimple size={14} className="mr-1" /> Subir archivo
                    </Button>
                    <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />
                  </div>
                  <div className="border-t pt-2">
                    <label className="text-[10px] font-semibold text-slate-500">Pegar enlace</label>
                    <Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." className="h-8 text-sm mt-0.5" />
                    <Input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="Nombre (opcional)" className="h-8 text-sm mt-1" />
                    <Button size="sm" className="h-8 text-xs mt-1.5" onClick={addLink} disabled={!linkUrl.trim()}>Insertar enlace</Button>
                  </div>
                </div>
              )}

              {/* Active Labels Display */}
              {(neg.label_ids || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1.5">Etiquetas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(neg.label_ids || []).map(lid => {
                      const lb = allLabels.find(l => l.id === lid);
                      return lb ? <span key={lid} className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: lb.color }}>{lb.name}</span> : null;
                    })}
                  </div>
                </div>
              )}

              {/* Dates Display */}
              {(neg.start_date || neg.due_date) && (
                <div className="flex gap-3">
                  {neg.start_date && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <Clock size={12} /> <span className="font-semibold">Inicio:</span> {format(new Date(neg.start_date + "T12:00:00"), "dd MMM yyyy", { locale: es })}
                    </div>
                  )}
                  {neg.due_date && (
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${new Date(neg.due_date) < new Date() ? "text-red-600" : "text-slate-600"}`}>
                      <Clock size={12} /> Vence: {format(new Date(neg.due_date + "T12:00:00"), "dd MMM yyyy", { locale: es })}
                      {new Date(neg.due_date) < new Date() && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded">VENCIDA</span>}
                    </div>
                  )}
                </div>
              )}

              {/* Members Display */}
              {(neg.members || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1.5">Miembros</p>
                  <div className="flex flex-wrap gap-1.5">
                    {neg.members.map(m => (
                      <div key={m.id} className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded-lg">
                        <div className="w-5 h-5 rounded-full bg-blue-600 text-white text-[8px] font-bold flex items-center justify-center">{(m.name || "?").slice(0, 2).toUpperCase()}</div>
                        <span className="text-[10px] font-semibold text-blue-800">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="space-y-2">
                {neg.company_name && <div className="flex items-center gap-2"><Buildings size={14} className="text-slate-400" /><div><p className="text-[10px] text-slate-400">Empresa</p><p className="text-sm font-semibold text-slate-800">{neg.company_name}</p></div></div>}
                {neg.contact_name && <div className="flex items-center gap-2"><User size={14} className="text-blue-500" /><div><p className="text-[10px] text-slate-400">Contacto</p><p className="text-sm font-semibold text-blue-600 cursor-pointer hover:underline" onClick={() => { onClose(); neg.contact_id && onNavigateToContact?.(neg.contact_id); }}>{neg.contact_name}</p></div></div>}
              </div>

              {/* Notes */}
              {neg.notes && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Notas</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">{neg.notes}</div>
                </div>
              )}

              {/* Checklists */}
              {(neg.checklists || []).map(cl => {
                const total = cl.items?.length || 0;
                const done = cl.items?.filter(i => i.checked).length || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={cl.id} className="space-y-2" data-testid={`checklist-${cl.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5"><CheckSquare size={14} className="text-blue-600" /> {cl.title}</p>
                      <button onClick={() => deleteChecklist(cl.id)} className="text-slate-300 hover:text-red-500"><Trash size={12} /></button>
                    </div>
                    {total > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 font-mono w-8">{pct}%</span>
                        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${done === total ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {(cl.items || []).map(item => (
                        <div key={item.id}
                          draggable
                          onDragStart={() => handleCheckDragStart(cl.id, item.id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => handleCheckDrop(cl.id, item.id)}
                          className={`flex items-center gap-2 group py-0.5 cursor-grab active:cursor-grabbing ${dragCheckItem?.itemId === item.id ? "opacity-40" : ""}`}>
                          <DotsSixVertical size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 shrink-0 cursor-grab" />
                          <button onClick={() => toggleCheckItem(cl.id, item.id, item.checked)}
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${item.checked ? "bg-blue-600 border-blue-600" : "border-slate-300 hover:border-blue-400"}`}>
                            {item.checked && <Check size={10} weight="bold" className="text-white" />}
                          </button>
                          <span className={`flex-1 text-xs ${item.checked ? "line-through text-slate-400" : "text-slate-700"}`}>{item.text}</span>
                          <button onClick={() => deleteCheckItem(cl.id, item.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash size={10} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <Input value={newItemText[cl.id] || ""} onChange={e => setNewItemText(prev => ({ ...prev, [cl.id]: e.target.value }))}
                        placeholder="Agregar item..." className="h-7 text-xs flex-1"
                        onKeyDown={e => e.key === "Enter" && addChecklistItem(cl.id)} />
                      <Button size="sm" className="h-7 text-xs px-2" onClick={() => addChecklistItem(cl.id)}><Plus size={12} /></Button>
                    </div>
                  </div>
                );
              })}

              {/* Attachments */}
              {(neg.attachments || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1.5 flex items-center gap-1"><Paperclip size={12} /> Adjuntos ({neg.attachments.length})</p>
                  <div className="space-y-1.5">
                    {neg.attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg group">
                        {att.type === "link" ? <LinkSimple size={14} className="text-blue-500 shrink-0" /> : <Paperclip size={14} className="text-slate-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-blue-600 hover:underline truncate block">{att.name}</a>
                          <p className="text-[10px] text-slate-400">{att.created_by_name} · {att.created_at ? format(new Date(att.created_at), "dd MMM", { locale: es }) : ""}</p>
                        </div>
                        <button onClick={() => deleteAttachment(att.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              <div data-testid="comments-section">
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1"><ChatCircle size={12} /> Comentarios</p>
                <div className="flex gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                    {(user?.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Escribe un comentario..." className="text-xs min-h-[50px]" />
                    {newComment.trim() && <Button size="sm" className="h-7 text-xs mt-1" onClick={addComment}>Comentar</Button>}
                  </div>
                </div>
                <div className="space-y-3">
                  {comments.map(c => (
                    <div key={c.id} className="flex gap-2 group">
                      <div className="w-7 h-7 rounded-full bg-slate-400 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                        {(c.user_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800">{c.user_name}</span>
                          <span className="text-[10px] text-slate-400">{c.created_at ? format(new Date(c.created_at), "dd MMM yyyy, HH:mm", { locale: es }) : ""}</span>
                        </div>
                        <p className="text-xs text-slate-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                        {(c.user_id === user?.id || user?.role === "admin") && (
                          <button onClick={() => deleteComment(c.id)} className="opacity-0 group-hover:opacity-100 text-[10px] text-red-400 hover:text-red-600 mt-0.5">Eliminar</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Activity History */}
              <div data-testid="neg-history">
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1"><ClockCounterClockwise size={12} /> Actividad ({history.length})</p>
                <div className="space-y-0">
                  {history.map((h, i) => (
                    <div key={h.id || i} className="flex gap-2 py-1.5 border-b border-slate-100 last:border-0">
                      <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 text-[7px] font-bold flex items-center justify-center shrink-0 mt-0.5">{(h.user_name || "?")[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-slate-600">{h.details}</p>
                        <p className="text-[9px] text-slate-400">{h.user_name} · {h.created_at ? (() => { try { return format(new Date(h.created_at), "dd MMM, HH:mm", { locale: es }); } catch { return ""; }})() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Small helper: Dates panel
function DatesPanel({ neg, onSave, onClose }) {
  const [startDate, setStartDate] = useState(neg.start_date || "");
  const [dueDate, setDueDate] = useState(neg.due_date || "");
  return (
    <div className="border rounded-xl p-3 space-y-3 bg-slate-50" data-testid="dates-panel">
      <p className="text-xs font-bold text-slate-700">Fechas</p>
      <div>
        <label className="text-[10px] font-semibold text-slate-500">Fecha de inicio</label>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-sm mt-0.5" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500">Fecha de vencimiento</label>
        <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="h-8 text-sm mt-0.5" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-8 text-xs" onClick={() => { onSave(startDate, dueDate); onClose(); }}>Guardar</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}

// Small helper: New label form
function NewLabelForm({ headers, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6366f1");
  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/negociaciones/labels`, { name, color }, { headers });
      setName(""); onCreated?.();
      toast.success("Etiqueta creada");
    } catch { toast.error("Error"); }
  };
  return (
    <div className="flex items-center gap-2 border-t pt-2 mt-2">
      <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-7 h-7 rounded border cursor-pointer shrink-0" />
      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nueva etiqueta..." className="h-7 text-xs flex-1" />
      <Button size="sm" className="h-7 text-xs px-2" onClick={handleCreate}><Plus size={12} /></Button>
    </div>
  );
}
